import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Space,
  Table,
  Input,
  Form,
  DatePicker,
  Button,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { apiHandler } from 'utils/api';
import { CustomNotification } from 'common/components';
import {
  getTraceableComment,
  saveTraceableComment,
} from 'services/traceable-comment';
import { useSelector } from 'react-redux';
import selectorUser from '@redux/user/selectors';
import './TraceableComments.less';
import {
  useCheckIsMemberSupplier,
  useCheckIsSuperAdmin,
  useGetProductFullView,
} from 'hooks';
import { useCheckAllowViewQaCommentWorkflow } from 'pages/qa-spec/hooks';
import { useSafeProductIdParam } from 'hooks/useProductFullView';
import { useUpdateCommentCountOnTabClick } from './hooks';

const { RangePicker } = DatePicker;

const TraceableComments = ({ productId, isTabActive }) => {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());
  const [savingRowId, setSavingRowId] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const { id } = useSafeProductIdParam();

  const { productFull } = useGetProductFullView({
    productId: id,
  });
  const ownerId = productFull?.ownerId;

  const isSuperAdmin = useCheckIsSuperAdmin();
  const isMemberSupplierUser = useCheckIsMemberSupplier();
  const isQAUser = useCheckAllowViewQaCommentWorkflow();
  const canEdit = isQAUser && !isSuperAdmin; // Only QA user can edit

  const { handleCommentsActionClick } = useUpdateCommentCountOnTabClick({
    productId,
    ownerId,
  });

  useEffect(() => {
    if (isTabActive && productId) {
      fetchData();
    }
  }, [productId, isTabActive]);

  const fetchData = async (fromDate, toDate) => {
    setLoading(true);
    const params = { productId };
    if (fromDate) params.FromDate = fromDate;
    if (toDate) params.ToDate = toDate;
    await apiHandler({
      service: getTraceableComment,
      params: {
        productId,
        FromDate: fromDate,
        ToDate: toDate,
        IsSupplierComment: isMemberSupplierUser ? true : false,
      },
      successCallback: (responseData) => {
        if (responseData?.traceableComments?.length > 0) {
          const response = responseData.traceableComments;

          const grouped = {};
          response.forEach((item) => {
            const key = `${item.qaSpecName}-${item.qaSpecFieldName}`;
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(item);
          });

          Object.keys(grouped).forEach((key) => {
            grouped[key].sort((a, b) => {
              const dateA = new Date(
                a.qaLastUpdated || a.supplierLastUpdated || 0
              );
              const dateB = new Date(
                b.qaLastUpdated || b.supplierLastUpdated || 0
              );
              return dateB - dateA;
            });
          });

          const formattedData = [];

          Object.entries(grouped).forEach(([key, entries]) => {
            entries.forEach((item, index) => {
              const isLatest = index === 0;
              formattedData.push({
                key: item.id,
                id: item.id,
                qaSpecName: item.qaSpecName || '',
                qaSpecFieldName: item.qaSpecFieldName || '',
                supplierComment: item.supplierComment || '',
                supplierName: item.supplierName || '',
                supplierLastUpdated: item.supplierLastUpdated || '',
                qaComment: item.qaComment || '',
                qaName: item.qaName || '',
                qaLastUpdated: item.qaLastUpdated || '',
                productStatus: item.productStatus,
                visibility: isLatest,
                isLatest,
                isEditing: false,
                editMade: false,
              });
            });
          });

          const groupedEntries = Object.values(
            formattedData.reduce((acc, item) => {
              const key = `${item.qaSpecName}-${item.qaSpecFieldName}`;
              if (!acc[key]) acc[key] = [];
              acc[key].push(item);
              return acc;
            }, {})
          );

          groupedEntries.sort((a, b) => {
            const latestA = a.find((i) => i.isLatest);
            const latestB = b.find((i) => i.isLatest);

            // Handle null or undefined cases for latestA and latestB
            const dateA = new Date(
              latestA
                ? isQAUser
                  ? latestA.qaLastUpdated || latestA.supplierLastUpdated || 0
                  : latestA.supplierLastUpdated || latestA.qaLastUpdated || 0
                : 0
            );

            const dateB = new Date(
              latestB
                ? isQAUser
                  ? latestB.qaLastUpdated || latestB.supplierLastUpdated || 0
                  : latestB.supplierLastUpdated || latestB.qaLastUpdated || 0
                : 0
            );

            return dateB - dateA;
          });

          const sortedFormattedData = groupedEntries.flat();

          setData(sortedFormattedData);
          setFilteredData(sortedFormattedData);
        } else {
          setData([]);
          setFilteredData([]);
        }
      },

      failCallback: () => CustomNotification.error('Failed to fetch data'),
      onFinally: () => setLoading(false),
    });
  };

  const filterAndSetData = (allData, searchText) => {
    if (!searchText.trim()) {
      setFilteredData(
        allData.filter((item) => item.isLatest || item.visibility)
      );
      return;
    }

    const groupedByKey = allData.reduce((acc, item) => {
      const key = `${item.qaSpecName}-${item.qaSpecFieldName}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const filtered = Object.entries(groupedByKey).flatMap(([key, items]) => {
      const latest = items.find((i) => i.isLatest);
      const matchesSearch = (latest.qaSpecName + latest.qaSpecFieldName)
        .toLowerCase()
        .includes(searchText.toLowerCase());

      if (matchesSearch) {
        // Show all rows of this group: latest + history
        return items.map((item) => ({
          ...item,
          visibility: !item.isLatest || item.visibility,
        }));
      }

      return [];
    });

    setFilteredData(filtered);
  };

  const handleSearch = () => {
    let fromDate = '';
    let toDate = '';
    if (dateRange.length === 2) {
      fromDate = dateRange[0].endOf('day').toDate().toISOString();
      toDate = dateRange[1].endOf('day').toDate().toISOString();
    }
    fetchData(fromDate, toDate);
  };

  const handleReset = () => {
    setSearchText('');
    setDateRange([]);
    form.resetFields();
    fetchData();
    setAllExpanded(false);
  };

  const toggleVisibility = (record) => {
    const key = `${record.qaSpecName}-${record.qaSpecFieldName}`;
    const anyVisible = filteredData.some(
      (item) =>
        `${item.qaSpecName}-${item.qaSpecFieldName}` === key &&
        item.visibility &&
        item.id !== record.id
    );
    setFilteredData((prev) =>
      prev.map((item) => {
        const itemKey = `${item.qaSpecName}-${item.qaSpecFieldName}`;
        if (itemKey === key && item.id !== record.id) {
          return { ...item, visibility: !anyVisible };
        }
        return item;
      })
    );
  };

  const toggleAllRows = () => {
    const newState = !allExpanded;
    setAllExpanded(newState);
    setFilteredData(
      data.map((item) =>
        item.isLatest
          ? { ...item, visibility: false }
          : { ...item, visibility: newState }
      )
    );
  };

  const toggleEdit = (key) => {
    setFilteredData((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;

        const isEnteringEdit = !item.isEditing;
        return {
          ...item,
          isEditing: !item.isEditing,
          editMade: false,
          originalQaComment: isEnteringEdit
            ? item.qaComment
            : item.originalQaComment,
        };
      })
    );
  };

  const handleCancel = (key) => {
    setFilteredData((prev) =>
      prev.map((item) =>
        item.key === key
          ? {
              ...item,
              isEditing: false,
              editMade: false,
              qaComment: item.originalQaComment ?? item.qaComment,
            }
          : item
      )
    );
  };

  const handleChange = (key, field, value) => {
    setFilteredData((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value, editMade: true } : item
      )
    );
  };

  const handleSave = async (toggleEditMode) => {
    const editedItem = filteredData.find((item) => item.isEditing);
    if (!canEdit || !editedItem) return;

    if (!editedItem.qaComment || editedItem.qaComment.trim() === '') {
      CustomNotification.warning('QA Comment cannot be empty');
      return;
    }

    if (savingRowId === editedItem.id) return; // Prevent duplicate

    setSavingRowId(editedItem.id); // Lock the row

    const payload = {
      id: editedItem.id,
      qaComment: editedItem.qaComment,
      qaUpdatedBy: userInfo?.id,
    };

    await apiHandler({
      service: saveTraceableComment,
      params: payload,
      successCallback: () => {
        CustomNotification.success('QA comment saved successfully');
        toggleEditMode();
        fetchData();
      },
      failCallback: () => CustomNotification.error('Failed to save comment'),
      onFinally: () => setSavingRowId(null), // Unlock the row
    });
  };

  const columns = [
    {
      title: 'QA Spec',
      dataIndex: 'qaSpecName',
      key: 'qaSpecName',
    },
    {
      title: 'Field Name',
      dataIndex: 'qaSpecFieldName',
      key: 'qaSpecFieldName',
      render: (text) => <div className='traceable-comment-display'>{text}</div>,
    },
    {
      title: 'Supplier Comment',
      dataIndex: 'supplierComment',
      key: 'supplierComment',
      render: (text) => (
        <Tooltip title={text}>
          <div className='traceable-comment-display traceable-comment-cell'>
            {text || ''}
          </div>
        </Tooltip>
      ),
      width: 300,
    },
    {
      title: 'Updated by',
      dataIndex: 'supplierName',
      key: 'updatedBySupplier',
    },
    {
      title: 'Last Updated',
      dataIndex: 'supplierLastUpdated',
      key: 'lastUpdatedBySupplier',
      render: (text) => (text ? new Date(text).toLocaleString() : ''),
    },
    {
      title: 'QA Comment',
      dataIndex: 'qaComment',
      key: 'qaComment',
      render: (text, record) =>
        record.isEditing && isQAUser ? (
          <Input.TextArea
            className='traceable-comment-text traceable-comment-cell'
            value={text}
            onChange={(e) =>
              handleChange(record.key, 'qaComment', e.target.value)
            }
            autoSize
          />
        ) : (
          <Tooltip title={text}>
            <div className='traceable-comment-display traceable-comment-cell'>
              {text || ''}
            </div>
          </Tooltip>
        ),
      width: 300,
    },
    {
      title: 'Updated By QA',
      dataIndex: 'qaName',
      key: 'updatedByQa',
      render: (text) => (text ? text : ''),
    },
    {
      title: 'Last Updated QA',
      dataIndex: 'qaLastUpdated',
      key: 'lastUpdatedByQa',
      render: (text) => (text ? new Date(text).toLocaleString() : ''),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        if (record.productStatus === 'Closed') return null;
        if (!canEdit || !record.isLatest) return null;
        return record.isEditing ? (
          <Space>
            <Button
              icon={<SaveOutlined />}
              type='primary'
              disabled={!record.editMade || savingRowId === record.id}
              loading={savingRowId === record.id}
              onClick={() => handleSave(() => toggleEdit(record.key))}
            />
            <Button
              disabled={savingRowId === record.id}
              icon={<CloseOutlined />}
              onClick={() => handleCancel(record.key)}
            />
          </Space>
        ) : (
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              handleCommentsActionClick();
              toggleEdit(record.key);
            }}
          />
        );
      },
    },
    {
      title: (
        <div className='visibility-header'>
          <span>Visibility</span>
          <Button
            type='link'
            onClick={toggleAllRows}
            className='visibility-button'
          >
            {allExpanded ? 'Collapse All' : 'Show All'}
          </Button>
        </div>
      ),
      key: 'visibility',
      render: (_, record) => {
        const comboKey = `${record.qaSpecName}-${record.qaSpecFieldName}`;
        const latestForCombo = data.find(
          (r) =>
            `${r.qaSpecName}-${r.qaSpecFieldName}` === comboKey && r.isLatest
        );
        const hasHistory =
          data.filter(
            (r) => `${r.qaSpecName}-${r.qaSpecFieldName}` === comboKey
          ).length > 1;
        const isExpanded = filteredData.some(
          (r) =>
            `${r.qaSpecName}-${r.qaSpecFieldName}` === comboKey &&
            !r.isLatest &&
            r.visibility
        );

        return record.id === latestForCombo?.id && hasHistory ? (
          <Button onClick={() => toggleVisibility(record)}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        ) : null;
      },
      width: 150,
    },
  ];

  const groupedData = Object.values(
    filteredData.reduce((acc, item) => {
      const key = `${item.qaSpecName}-${item.qaSpecFieldName}`;
      if (!acc[key]) acc[key] = [];
      if (item.isLatest || item.visibility) acc[key].push(item);
      return acc;
    }, {})
  ).flat();

  return (
    <div className='traceable-comments'>
      <Form form={form} layout='vertical'>
        <Row className='filter-row'>
          <Col>
            <Form.Item name='searchbar'>
              <Input
                placeholder='Search QA Spec or Field Name'
                value={searchText}
                onChange={(e) => {
                  const newSearchText = e.target.value;
                  setSearchText(newSearchText);
                  filterAndSetData(data, newSearchText);
                }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item name='range'>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [])}
                format='YYYY-MM-DD'
                disabled={loading}
              />
            </Form.Item>
          </Col>
          <Col className='action-buttons'>
            <Form.Item name='action-buttonOne'>
              <Button
                type='primary'
                icon={<SearchOutlined />}
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
            </Form.Item>
            <Form.Item name='action-buttontwo'>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <Table
          dataSource={groupedData}
          columns={columns}
          loading={loading}
          rowKey='key'
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: groupedData.length,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '150', '200'],
            position: ['bottomLeft'],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
        />
      </Form>
    </div>
  );
};

export default TraceableComments;
